import merge from 'lodash/merge';
import mergeWith from 'lodash/mergeWith';
import union from 'lodash/union';
import unionWith from 'lodash/unionWith';
import isEqual from 'lodash/isEqual';
import { combineReducers } from 'redux';

import {
  FETCH_ENTITY_SUCCESS,
  FETCH_ENTITY_SINGLE_SUCCESS,
  UPDATE_ENTITY_SINGLE_SUCCESS,
  CREATE_ENTITY_SINGLE_SUCCESS,
  FETCH_PAGINATED_ENTITY_SUCCESS,
  FETCH_FILTERED_ENTITY_SUCCESS,
  DELETE_ENTITY_SINGLE_SUCCESS,
  FETCH_PARTIAL_ENTITY_SUCCESS,
  FETCH_ENTITY_PER_DAY_SUCCESS,
} from '@store/entity/actions';

const isSameSpa = (objValue, srcValue, key) => {
  if (key === 'availability') {
    /*
     * While refetching availabilities are being duplicated
     * by normal merge, so they require to be treated specially.
     */
    return isEqual(objValue.startDateTime, srcValue.startDateTime);
  }

  return isEqual(objValue, srcValue);
};

const mergeArrays = (objValue, srcValue, key) => {
  // concatenate arrays without duplicated objects,
  if (Array.isArray(objValue)) {
    // eslint-disable-next-line max-len
    return unionWith(srcValue, objValue, (customizerObjValue, customizerSrcValue) =>
      isSameSpa(customizerObjValue, customizerSrcValue, key)
    );
  }

  return undefined;
};

const paginatedEntities = ['spaDays', 'spaTreatments', 'facilities', 'vocuhers', 'events'];

export const createReducerForEntity = (entityName) => {
  const byId = (state = {}, action) => {
    const { payload } = action;
    switch (action.type) {
      case FETCH_ENTITY_SINGLE_SUCCESS:
      case CREATE_ENTITY_SINGLE_SUCCESS:
      case FETCH_ENTITY_PER_DAY_SUCCESS:
      case FETCH_ENTITY_SUCCESS: {
        if (payload.entities && payload.entities[entityName]) {
          if (payload.isOverwrite) {
            return payload.entities[entityName];
          }
          if (paginatedEntities.includes(entityName)) {
            // enable merging availability arrays for spa entities in lodash deep merge
            return mergeWith({}, state, payload.entities[entityName], mergeArrays);
          }

          return merge({}, state, payload.entities[entityName]);
        }
        return state;
      }
      case UPDATE_ENTITY_SINGLE_SUCCESS: {
        if (payload.entities && payload.entities[entityName]) {
          const updatedEntity = payload.entities[entityName];
          return {
            ...state,
            ...updatedEntity,
          };
        }
        return state;
      }
      case FETCH_PAGINATED_ENTITY_SUCCESS:
      case FETCH_FILTERED_ENTITY_SUCCESS: {
        if (payload.data.entities && payload.data.entities[entityName]) {
          return merge({}, state, payload.data.entities[entityName]);
        }
        return state;
      }
      case DELETE_ENTITY_SINGLE_SUCCESS: {
        if (payload[entityName] && payload[entityName].id) {
          return {
            ...state,
            [payload[entityName].id]: null,
          };
        }
        return state;
      }
      case FETCH_PARTIAL_ENTITY_SUCCESS: {
        if (payload.entities && payload.entities[entityName]) {
          const staleKey = Object.keys(payload.entities[entityName])[0];
          const { [staleKey]: _, ...rest } = state;
          return {
            ...rest,
            ...payload.entities[entityName],
          };
        }
        return state;
      }
      default: {
        return state;
      }
    }
  };
  const allIds = (state = [], action) => {
    const { payload } = action;
    switch (action.type) {
      case FETCH_ENTITY_SUCCESS: {
        if (payload.result && payload.result[entityName]) {
          return union(state, payload.result[entityName]);
        }
        return state;
      }
      case UPDATE_ENTITY_SINGLE_SUCCESS:
      case CREATE_ENTITY_SINGLE_SUCCESS:
      case FETCH_ENTITY_PER_DAY_SUCCESS:
      case FETCH_ENTITY_SINGLE_SUCCESS: {
        if (payload.result && payload.result[entityName]) {
          return union(state, [payload.result[entityName]]);
        }
        return state;
      }
      case FETCH_PAGINATED_ENTITY_SUCCESS: {
        if (payload.data.result && payload.data.result[entityName]) {
          return union(state, payload.data.result[entityName]);
        }
        return state;
      }
      case DELETE_ENTITY_SINGLE_SUCCESS: {
        if (payload[entityName] && payload[entityName].id) {
          return state.filter((id) => id !== payload[entityName].id);
        }
        return state;
      }
      default: {
        return state;
      }
    }
  };

  return combineReducers({
    byId,
    allIds,
  });
};

export default combineReducers({
  properties: createReducerForEntity('properties'),
  homepages: createReducerForEntity('homepages'),
  categories: createReducerForEntity('categories'),
  products: createReducerForEntity('products'),
  pages: createReducerForEntity('pages'),
  integrations: createReducerForEntity('integrations'),
  packagings: createReducerForEntity('packagings'),
  facilities: createReducerForEntity('facilities'),
  spaTreatments: createReducerForEntity('spaTreatments'),
  spaPackages: createReducerForEntity('spaPackages'),
  spaPackagesAll: createReducerForEntity('spaPackagesAll'),
  spaClasses: createReducerForEntity('spaClasses'),
  spaClassesTimeslots: createReducerForEntity('spaClassesTimeslots'),
  postages: createReducerForEntity('postages'),
  options: createReducerForEntity('options'),
});
